import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import log from "loglevel";
import { Container, Row, Col } from "react-bootstrap";
import {
  decodeJwtAction,
  getAccountsAction,
  getAnalysesAction,
  getTeamsAction,
  getUsersAction,
  getAccountUsersAction,
  getTeamUsersAction,
  getRolesAction,
  getCurrentUserAccountOwnerAction,
} from "store/actions/actionCreators";
import DashboardSidebar from "components/sidebar/DashboardSidebar";
import MainNav from "components/nav/MainNav";
import UsersCard from "components/dashboard/user/UsersCard";
import AccountUsersCard from "components/dashboard/account/AccountUsersCard";
import TeamUsersCard from "components/dashboard/team/TeamUsersCard";
import {
  getAccountsReducerResponse,
  getUsersReducerResponse,
  getAccountUsersReducerResponse,
  getTeamUsersReducerResponse,
  getTeamsReducerResponse,
  getRolesReducerResponse,
  getJwtReducerResponse,
  getIsCurrentUserAccountOwnerReducerResponse,
} from "store/selectors";
import {
  Account,
  User,
  AccountUser,
  TeamUser,
  Team,
  Role,
  JWT,
  CurrentUserAccountOwnerStatus,
} from "types";
import {
  administratorRoleId,
  accountOwnerRoleId,
  teamLeaderRoleId,
  memberRoleId,
} from "settings";
import * as routes from "routes";
import {
  InfoCard,
  InfoTableCard,
  Breadcrumb,
  BreadcrumbData,
  Page,
} from "@avlino-inc/alenza-react";
import { faUsers } from "@fortawesome/pro-light-svg-icons/faUsers";
import { faUsersClass } from "@fortawesome/pro-light-svg-icons/faUsersClass";
import { faUserFriends } from "@fortawesome/pro-light-svg-icons/faUserFriends";

const DashboardAccessControlPage = ({
  authed,
}: {
  authed: Promise<boolean>;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const accountsData: Account[] = useSelector((state) =>
    getAccountsReducerResponse(state, "data")
  );
  const usersData: User[] = useSelector((state) =>
    getUsersReducerResponse(state, "data")
  );
  const accountUsersData: AccountUser[] = useSelector((state) =>
    getAccountUsersReducerResponse(state, "data")
  );
  const teamUsersData: TeamUser[] = useSelector((state) =>
    getTeamUsersReducerResponse(state, "data")
  );
  const teamsData: Team[] = useSelector((state) =>
    getTeamsReducerResponse(state, "data")
  );
  const rolesData: Role[] = useSelector((state) =>
    getRolesReducerResponse(state, "data")
  );
  const jwtPayloadData: JWT = useSelector((state) =>
    getJwtReducerResponse(state, "data")
  );
  const isCurrentUserAccountOwnerData: CurrentUserAccountOwnerStatus = useSelector(
    (state) => getIsCurrentUserAccountOwnerReducerResponse(state, "data")
  );

  const isAdmin = (jwtPayload: JWT, validRoleId: number) => {
    return jwtPayload && jwtPayload.role_id === validRoleId;
  };

  const isAvlino = (jwtPayload: JWT) => {
	return jwtPayload?.email?.endsWith('@avlino.com')
  }

  const isAccountOwner = (ownerData: CurrentUserAccountOwnerStatus) => {
    return ownerData && ownerData.is_owner;
  };

  useEffect(() => {
    if (authed) {
      authed.then((allow) => {
        log.info("Allow: " + allow);
        if (allow) {
          log.info("Authenticated. Stay on page...");
        } else {
          log.info("Redirect back...");
          history.push(routes.LOGIN);
        }
      });
    } else {
      history.push(routes.LOGIN);
    }
  }, [authed, history]);

  useEffect(() => {
    log.info("DashboardAccessControlPage useEffect()...");
    dispatch(decodeJwtAction({}));
    dispatch(getAccountsAction({}));
    dispatch(getUsersAction({}));
    dispatch(getAccountUsersAction({}));
    dispatch(getTeamUsersAction({}));
    dispatch(getAnalysesAction({}));
    dispatch(getTeamsAction({}));
    dispatch(getRolesAction({}));
    dispatch(getCurrentUserAccountOwnerAction({}));
  }, [dispatch]);

  /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);
  
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showCreateAccountUserModal, setShowCreateAccountUserModal] = useState(
    false
  );
  const [showCreateTeamUserModal, setShowCreateTeamUserModal] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [showAccountUserSearch, setShowAccountUserSearch] = useState(false);
  const [showTeamUserSearch, setShowTeamUserSearch] = useState(false);

  const breadcrumbItems = useMemo<BreadcrumbData[]>(
    () => [
      {
        url: routes.HOME,
        label: "Command Center",
      },
      {
        url: routes.DASHBOARD_MAIN,
        label: "Dashboard",
      },
      {
        url: routes.DASHBOARD_ACCESS_CONTROL,
        label: "Access Control",
      },
    ],
    []
  );

  return (
    <div id="DashboardAccessControlPage">
      <MainNav
        first_name={jwtPayloadData && jwtPayloadData.first_name}
        last_name={jwtPayloadData && jwtPayloadData.last_name}
        username={jwtPayloadData && jwtPayloadData.username}
        email={jwtPayloadData && jwtPayloadData.email}
      />
      <div style={{ display: "flex" }}>
        <DashboardSidebar
          isAvlino={isAvlino(jwtPayloadData)}
          isAdmin={isAdmin(jwtPayloadData, administratorRoleId)}
        />
        <main id="page-wrap">
          <Container fluid={true} style={{ marginBottom: "100px" }}>
            <Page.Header>
              <Breadcrumb items={breadcrumbItems} onClickItem={history.push} />
            </Page.Header>
            <Row>
              <Col xs={12} xl={4}>
                <InfoCard
                  label="Users"
                  value={usersData?.length || 0}
                  color="bright-blue"
                  icon={faUsers}
                />
                <br />         <InfoTableCard
                  icon={faUsers}
                  color="bright-blue"
                  onNewClick={
                    isAdmin(jwtPayloadData, administratorRoleId) ||
                    isAccountOwner(isCurrentUserAccountOwnerData)
                      ? () => {
                          setShowCreateUserModal(!showCreateUserModal);
                        }
                      : undefined
                  }
                  onMenuClick={() =>
                    showUserSearch
                      ? setShowUserSearch(false)
                      : setShowUserSearch(true)
                  }
                >
                  <UsersCard
                    showCreateModal={showCreateUserModal}
                    setShowCreateModal={setShowCreateUserModal}
                    users={usersData}
                    roles={
                      rolesData &&
                      rolesData.filter((role) =>
                        [administratorRoleId, memberRoleId].includes(role.id)
                      )
                    }
                    compactView={false}
                    showSearch={showUserSearch}
                    userIsAdmin={isAdmin(jwtPayloadData, administratorRoleId)}
                    userIsAccountOwner={isAccountOwner(
                      isCurrentUserAccountOwnerData
                    )}
                  />
                </InfoTableCard>
                <br />
              </Col>
              <Col xs={12} xl={4}>
                <InfoCard
                  label="Account Connections"
                  value={accountUsersData?.length || 0}
                  color="ketchup"
                  icon={faUsersClass}
                />
                <br />
                <InfoTableCard
                  icon={faUsersClass}
                  color="ketchup"
                  onNewClick={
                    isAdmin(jwtPayloadData, administratorRoleId) ||
                    isAccountOwner(isCurrentUserAccountOwnerData)
                      ? () => {
                          setShowCreateAccountUserModal(
                            !showCreateAccountUserModal
                          );
                        }
                      : undefined
                  }
                  onMenuClick={() =>
                    showAccountUserSearch
                      ? setShowAccountUserSearch(false)
                      : setShowAccountUserSearch(true)
                  }
                >
                  <AccountUsersCard
                    showCreateModal={showCreateAccountUserModal}
                    setShowCreateModal={setShowCreateAccountUserModal}
                    accountUsers={accountUsersData}
                    accounts={accountsData}
                    users={usersData}
                    roles={
                      rolesData &&
                      rolesData.filter((role) =>
                        [accountOwnerRoleId, memberRoleId].includes(role.id)
                      )
                    }
                    showSearch={showAccountUserSearch}
                  />
                </InfoTableCard>
                <br />
              </Col>
              <Col xs={12} xl={4}>
                <InfoCard
                  label="Teammates"
                  value={teamUsersData?.length || 0}
                  color="eggplant"
                  icon={faUserFriends}
                />
                <br />
                <InfoTableCard
                  icon={faUserFriends}
                  color="eggplant"
                  onNewClick={
                    isAdmin(jwtPayloadData, administratorRoleId) ||
                    isAccountOwner(isCurrentUserAccountOwnerData)
                      ? () => {
                          setShowCreateTeamUserModal(!showCreateTeamUserModal);
                        }
                      : undefined
                  }
                  onMenuClick={() =>
                    showTeamUserSearch
                      ? setShowTeamUserSearch(false)
                      : setShowTeamUserSearch(true)
                  }
                >
                  <TeamUsersCard
                    showCreateModal={showCreateTeamUserModal}
                    setShowCreateModal={setShowCreateTeamUserModal}
                    teamUsers={teamUsersData}
                    accountUsers={accountUsersData}
                    teams={teamsData}
                    roles={
                      rolesData &&
                      rolesData.filter((role) =>
                        [teamLeaderRoleId, memberRoleId].includes(role.id)
                      )
                    }
                    showSearch={showTeamUserSearch}
                  />
                </InfoTableCard>
                <br />
              </Col>
            </Row>
          </Container>
        </main>
      </div>
    </div>
  );
};

export default DashboardAccessControlPage;
